/**
 * BATCH REVIEW FILTER TRANSITIONS — one place that decides what each filter change does to the others.
 *
 * These used to be inline decisions spread across three `onChange` handlers, and they disagreed with
 * each other. Changing Client cleared State but left Batch Date; changing State left Batch Date too.
 * That is backwards: the State options are global (they come from the filing-capability registry and
 * are not scoped to a client), so a selected State stays valid across a Client change — while the
 * Batch Date options ARE reloaded scoped to Client + State, so the selected day is the one value that
 * can genuinely stop existing. We were clearing the filter that survives and keeping the one that
 * goes stale.
 *
 * Keeping the transitions here rather than in the page means they can be tested as behaviour instead
 * of as source text: BatchFilingReview is a ~3,300-line component over MUI DataGrid, routing and auth
 * context, and standing that up to assert "the State is still selected" would cost far more than it
 * proves. The page holds one `useState` per filter, reads them into a value object, hands that object
 * to the transition, and writes the result back — so every path through the filter bar goes through a
 * function with a test.
 *
 * NOTE ON `partner` / `state`: these are deliberately generic. This module never inspects them, it
 * only carries or drops them, so it does not need to know about `PartnerOption` or `StateOption`.
 */

import { LifecycleTab } from './batchFilingLifecycle';

/** The filing-status tab strip's default. A cleared filter bar returns to it. */
export const DEFAULT_FILING_STATUS_TAB: LifecycleTab = 'ready';

/** `''` means "every day"; the page's `NO_BATCH_DATE` sentinel is just another selected value here. */
export const ALL_BATCH_DATES = '';

export interface BatchReviewFilterValues<P = unknown, S = unknown> {
    /** Selected Client, or null for "all clients". */
    partner: P | null;
    /** Selected State, or null for "all states". */
    state: S | null;
    /** Selected batch day, `ALL_BATCH_DATES` for every day, or the page's no-batch-date sentinel. */
    batchDate: string;
    insuredName: string;
    policyNumber: string;
    /**
     * Typed as the real union rather than `string` on purpose: the page writes this straight back into
     * `setFilingStatusFilter`, and a plain `string` here would force an unchecked cast at that seam --
     * which is exactly where a typo'd tab name would compile and then fail at runtime.
     */
    filingStatus: LifecycleTab;
}

/** Every filter at its default — what Clear Filters produces. */
export function clearedFilterValues<P = unknown, S = unknown>(): BatchReviewFilterValues<P, S> {
    return {
        partner: null,
        state: null,
        batchDate: ALL_BATCH_DATES,
        insuredName: '',
        policyNumber: '',
        filingStatus: DEFAULT_FILING_STATUS_TAB,
    };
}

/**
 * Changing the Client.
 *
 * PRESERVES the State. This is the behaviour change: a filer working Texas who switches from Client A
 * to Client B means "show me B's Texas book", not "throw me back to every state". The State options
 * are not client-scoped, so the selection is still a valid option for the new client. If the new
 * client has no policies in that state the page shows its normal empty state, which already tells the
 * filer to clear the Client / State — silently widening the search to all states would instead show a
 * screen full of rows the filer did not ask for and did not notice they were now looking at.
 *
 * CLEARS the Batch Date, because the day list is reloaded scoped to Client + State. A day that exists
 * for Client A need not exist for Client B, and a Select holding a value that is no longer among its
 * options renders blank while still being sent to the server as a filter.
 *
 * Leaves the two text filters alone: they are a free-text narrowing of whatever is on screen and are
 * meaningful against any client.
 */
export function filterValuesOnClientChange<P, S>(
    prev: BatchReviewFilterValues<P, S>,
    nextPartner: P | null,
): BatchReviewFilterValues<P, S> {
    return scopeChange(prev, { partner: nextPartner });
}

/**
 * Changing the State.
 *
 * Same reasoning as the Client change, for the same reason: `filingBatchDates` is queried on
 * `partnersId` AND `stateId`, so narrowing the state can strand the selected day just as changing the
 * client can. Without this, TX + 2026-08-31 followed by a switch to SD leaves 2026-08-31 selected,
 * absent from the reloaded options, and still applied server-side.
 */
export function filterValuesOnStateChange<P, S>(
    prev: BatchReviewFilterValues<P, S>,
    nextState: S | null,
): BatchReviewFilterValues<P, S> {
    return scopeChange(prev, { state: nextState });
}

/**
 * The shared rule behind both transitions: changing the SCOPE (Client or State) drops the Batch Date
 * and leaves everything else alone.
 *
 * Deliberately one implementation with two named wrappers. The named exports keep the call sites
 * readable, but the rule lives once -- and "the same concern handled differently on two paths" is the
 * precise shape of the defect this change exists to fix, so letting the two drift apart again would
 * reintroduce it. A future rule that genuinely applies to only one of them belongs in that wrapper,
 * above this call.
 */
function scopeChange<P, S>(
    prev: BatchReviewFilterValues<P, S>,
    patch: Partial<Pick<BatchReviewFilterValues<P, S>, 'partner' | 'state'>>,
): BatchReviewFilterValues<P, S> {
    return { ...prev, ...patch, batchDate: ALL_BATCH_DATES };
}

/**
 * Whether anything is narrowed, i.e. whether Clear Filters would actually do something. Drives the
 * button's disabled state so it does not offer a no-op reload of the default view.
 */
export function isAnyFilterActive(values: BatchReviewFilterValues): boolean {
    return (
        values.partner !== null
        || values.state !== null
        || values.batchDate !== ALL_BATCH_DATES
        || values.insuredName !== ''
        || values.policyNumber !== ''
        || values.filingStatus !== DEFAULT_FILING_STATUS_TAB
    );
}
